import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { type OrgSwitchTarget, useOrgSwitchState } from '@/lib/orgSwitch';
import { orgToneClass } from '@/lib/orgSwitcher';
import { getInitials } from '@/lib/studioIdentity';
import { cn } from '@/lib/utils';

const FADE_MS = 300;

/**
 * Covers Studio while it moves to another organization, and fades away once
 * the new organization's first page is drawn behind it. It sits outside the
 * part of Studio that is redrawn, so it stays put across the switch.
 */
export function OrgSwitchCover() {
  const { t } = useTranslation();
  const { phase, target } = useOrgSwitchState();
  const active = phase !== 'idle';
  const [shown, setShown] = useState<OrgSwitchTarget | undefined>();
  const [opaque, setOpaque] = useState(false);

  useEffect(() => {
    if (active && target) {
      setShown(target);
      const frame = window.requestAnimationFrame(() => setOpaque(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setOpaque(false);
    const timer = window.setTimeout(() => setShown(undefined), FADE_MS);
    return () => window.clearTimeout(timer);
  }, [active, target]);

  if (!shown) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed inset-0 z-[400] flex items-center justify-center bg-background transition-opacity ease-out motion-reduce:transition-none',
        opaque ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      style={{ transitionDuration: `${FADE_MS}ms` }}
    >
      <div
        className={cn(
          'flex flex-col items-center gap-4 text-center transition-transform ease-out motion-reduce:transition-none',
          opaque ? 'translate-y-0' : 'translate-y-1',
        )}
        style={{ transitionDuration: `${FADE_MS}ms` }}
      >
        <span
          className={cn('flex h-14 w-14 items-center justify-center rounded-2xl text-lg font-bold', orgToneClass(shown.workosId))}
          aria-hidden="true"
        >
          {getInitials(shown.name)}
        </span>
        <div>
          <p className="text-base font-semibold text-foreground">{t('nav.org-switcher.switching', { name: shown.name })}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('nav.org-switcher.switchingDetail')}</p>
        </div>
        <RapidSOSMark className="h-8 w-8" animation="route" />
      </div>
    </div>
  );
}
